import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, TokenManager } from "@/supabase/supabaseClient";
import { taskKeys } from "./useTasks";
import { useTenant } from "./useTenant";
import type { Task } from "@/types/task";
import type { RealtimeChannel } from "@supabase/supabase-js";

/**
 * Hook to subscribe to real-time task updates from Supabase
 * This will automatically update the task cache when any task is:
 * - Created (INSERT)
 * - Updated (UPDATE)
 * - Deleted (DELETE)
 */
export function useTasksRealtime() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  useEffect(() => {
    let channel: RealtimeChannel | null = null;
    let isMounted = true;

    const setup = async () => {
      // Ensure we have an authenticated session so realtime honors RLS
      const token = await TokenManager.getStoredTokenAsync();

      if (!token) {
        return;
      }

      await TokenManager.setSupabaseSession(token);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session || !isMounted) {
        return;
      }
      // Subscribe to all changes on the tasks table
      channel = supabase
        .channel("tasks-realtime")
        .on(
          "postgres_changes",
          {
            event: "*", // Listen to all events (INSERT, UPDATE, DELETE)
            schema: "public",
            table: "tasks",
            filter: tenantId ? `tenant_id=eq.${tenantId}` : undefined,
          },
          (payload) => {
            switch (payload.eventType) {
              case "INSERT": {
                queryClient.invalidateQueries({
                  queryKey: taskKeys.userTasks(tenantId || ""),
                });
                break;
              }

              case "UPDATE": {
                const updatedTask = payload.new as Task;
                queryClient.invalidateQueries({
                  queryKey: taskKeys.userTasks(tenantId || ""),
                });
                queryClient.invalidateQueries({
                  queryKey: taskKeys.byId(tenantId || "", updatedTask.id),
                });
                break;
              }

              case "DELETE": {
                const deletedTaskId = (payload.old as { id: string })?.id;
                if (!deletedTaskId) {
                  queryClient.invalidateQueries({
                    queryKey: taskKeys.userTasks(tenantId || ""),
                  });
                  break;
                }

                queryClient.removeQueries({
                  queryKey: taskKeys.byId(tenantId || "", deletedTaskId),
                });
                queryClient.setQueryData<Task[]>(
                  taskKeys.userTasks(tenantId || ""),
                  (oldTasks) => {
                    if (!oldTasks) return oldTasks;
                    return oldTasks.filter((task) => task.id !== deletedTaskId);
                  }
                );
                break;
              }
            }
          }
        )
        .subscribe((status, err) => {
          if (status === "CHANNEL_ERROR") {
            console.error("Failed to subscribe to task updates:", err);
          }
        });
    };

    void setup();

    // Cleanup subscription on unmount
    return () => {
      isMounted = false;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [queryClient, tenantId]);
}

/**
 * Hook to subscribe to real-time step updates from Supabase
 * This listens to all steps changes and invalidates relevant task queries
 */
export function useStepsRealtime(taskId?: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  useEffect(() => {
    let channel: RealtimeChannel | null = null;
    let isMounted = true;

    const setup = async () => {
      const token = await TokenManager.getStoredTokenAsync();

      if (!token) {
        return;
      }

      await TokenManager.setSupabaseSession(token);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session || !isMounted) {
        return;
      }

      const channelName = taskId
        ? `steps-${taskId}-realtime`
        : "steps-global-realtime";

      // Build subscription config
      const subscriptionConfig: {
        event: "*";
        schema: string;
        table: string;
        filter?: string;
      } = {
        event: "*",
        schema: "public",
        table: "steps",
      };

      if (taskId) {
        subscriptionConfig.filter = `task_id=eq.${taskId}`;
      }

      channel = supabase
        .channel(channelName)
        .on("postgres_changes", subscriptionConfig, (payload) => {
          const affectedTaskId =
            (payload.new as { task_id?: string })?.task_id ||
            (payload.old as { task_id?: string })?.task_id;

          if (affectedTaskId) {
            queryClient.invalidateQueries({
              queryKey: taskKeys.byId(tenantId || "", affectedTaskId),
            });
          }

          queryClient.invalidateQueries({
            queryKey: taskKeys.userTasks(tenantId || ""),
          });
        })
        .subscribe((status, err) => {
          if (status === "CHANNEL_ERROR") {
            console.error("Failed to subscribe to step updates:", err);
          }
        });
    };

    void setup();

    return () => {
      isMounted = false;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [queryClient, taskId, tenantId]);
}
